import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Trash2 } from 'lucide-react';
import { adminController, adminPasskeyController } from '@/api/generated/endpoints';
import { BizError, errorMessage } from '@/api/errors';
import { unwrap } from '@/api/http';
import { useAuth } from '@/auth/AuthProvider';
import { formatDateTime } from '@/lib/format';
import { useResult, useResultMutation } from '@/lib/query';
import { Alert, Badge, Button, DescriptionList, Field, IconButton, NumberInput, PageHeader, Section, toast, useConfirm } from '@/ui';
import { addPasskey, isPasskeyCancel, passkeysSupported, STALE_SIGN_IN } from './passkey';
import { PasswordInput } from './PasswordInput';
import { passwordProblem } from './passwordPolicy';

/** The signed-in admin's profile, phone change and password change (which signs out every device). */
export function ProfilePage() {
  const { t } = useTranslation('auth', { keyPrefix: 'profile' });
  const { me, signOut } = useAuth();
  const form = useForm<{ password: string; confirm: string }>({ defaultValues: { password: '', confirm: '' } });
  const change = useResultMutation((password: string) => adminController.update({ updateRequestDTO: { password } }), {
    success: t('changed'),
    onSuccess: signOut,
  });
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <PageHeader title={t('title')} />
      <Section title={t('account')}>
        <DescriptionList
          columns={2}
          items={[
            { label: t('name'), value: me?.name },
            { label: t('email'), value: me?.email },
            { label: t('phone'), value: me?.phone ? `+${me.countryCode ?? 1} ${me.phone}` : '—' },
            {
              label: t('roles'),
              span: 'full',
              value: (me?.roles ?? []).length > 0 && (
                <span className="flex flex-wrap gap-1">
                  {(me?.roles ?? []).map((r) => (
                    <Badge key={r.id}>{r.name}</Badge>
                  ))}
                </span>
              ),
            },
          ]}
        />
      </Section>
      <PasskeysSection />
      <PhoneSection />
      <Section title={t('changePassword')} description={t('changePasswordHint')}>
        <form noValidate onSubmit={form.handleSubmit((v) => change.mutate(v.password))} className="flex max-w-sm flex-col gap-4">
          {/* typed twice, and either can be shown: a typo here would lock the admin out */}
          <Field label={t('newPassword')} error={form.formState.errors.password?.message}>
            <PasswordInput
              autoComplete="new-password"
              {...form.register('password', {
                required: t('passwordRequired'),
                validate: (v) => {
                  const problem = passwordProblem(v, me?.email);
                  return !problem || t(`password_${problem}`);
                },
              })}
            />
          </Field>
          <Field label={t('confirmPassword')} error={form.formState.errors.confirm?.message}>
            <PasswordInput
              autoComplete="new-password"
              {...form.register('confirm', {
                required: t('confirmRequired'),
                validate: (v, all) => v === all.password || t('passwordsDiffer'),
              })}
            />
          </Field>
          <Button type="submit" loading={change.isPending} className="self-start">
            {t('submit')}
          </Button>
        </form>
      </Section>
    </div>
  );
}

/**
 * The admin's passkeys: recommended, never required. Adding one needs a recent sign-in (the
 * gateway refuses an older session), so a stale session is asked to sign in again.
 */
function PasskeysSection() {
  const { t } = useTranslation('auth', { keyPrefix: 'passkey' });
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const list = useResult(['auth', 'passkeys'], () => adminPasskeyController.list());
  const [adding, setAdding] = useState(false);
  // why the gateway wants a new sign-in first (a stale session, or a passkey sign-in to add another)
  const [stale, setStale] = useState<string | null>(null);
  const refused = (err: unknown) => {
    if (err instanceof BizError && err.code === STALE_SIGN_IN) setStale(err.message);
    else if (!isPasskeyCancel(err)) toast.error(errorMessage(err));
  };
  const remove = useMutation({
    mutationFn: async (id: number) => unwrap(await adminPasskeyController.delete({ passkeyId: id })),
    onSuccess: () => {
      toast.success(t('removed'));
      qc.invalidateQueries({ queryKey: ['auth', 'passkeys'] });
    },
    onError: refused,
  });
  const supported = passkeysSupported();
  const passkeys = list.data ?? [];
  // the account menu's "Set up a passkey" links here
  const { hash } = useLocation();
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hash === '#passkeys') anchor.current?.scrollIntoView?.({ block: 'start' });
  }, [hash]);

  const add = async () => {
    setAdding(true);
    try {
      await addPasskey();
      toast.success(t('added'));
      await qc.invalidateQueries({ queryKey: ['auth', 'passkeys'] });
    } catch (err) {
      refused(err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={anchor} id="passkeys" className="scroll-mt-4">
      <Section
        title={t('title')}
        description={t('description')}
        actions={
          supported && (
            <Button variant={passkeys.length === 0 ? 'primary' : 'secondary'} icon={<Fingerprint />} loading={adding} onClick={add}>
              {t('add')}
            </Button>
          )
        }
      >
        <div className="flex flex-col gap-3">
          {stale && (
            <Alert
              tone="warning"
              title={stale}
              action={
                <Button size="sm" onClick={signOut}>
                  {t('signInAgain')}
                </Button>
              }
            />
          )}
          {!supported && <Alert tone="info" title={t('unsupported')} />}
          {list.isSuccess && passkeys.length === 0 && (
          <>
            <p className="m-0 text-base text-ink">{t('none')}</p>
            {supported && <p className="m-0 text-sm text-ink-muted">{t('offerWhere')}</p>}
          </>
        )}
          {passkeys.length > 0 && (
            <ul className="m-0 flex list-none flex-col divide-y divide-line p-0">
              {passkeys.map((p) => (
                <li key={p.id} className="flex items-center gap-3 py-2">
                  <Fingerprint className="size-5 shrink-0 text-ink-muted" aria-hidden />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-2 font-medium text-ink">
                      {p.name}
                      {p.synced && <Badge tone="info">{t('synced')}</Badge>}
                    </span>
                    <span className="text-sm text-ink-muted">
                      {t('addedOn', { date: formatDateTime(p.created) })} ·{' '}
                      {p.lastUsed ? t('lastUsed', { date: formatDateTime(p.lastUsed) }) : t('neverUsed')}
                    </span>
                  </div>
                  <IconButton
                    label={t('remove', { name: p.name })}
                    icon={<Trash2 />}
                    onClick={async () => {
                      const ok = await confirm({ title: t('removeTitle', { name: p.name }), description: t('removeBody'), confirmLabel: t('removeConfirm') });
                      if (ok && p.id != null) remove.mutate(p.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
    </div>
  );
}

interface PhoneValues {
  countryCode: number | null;
  phone: number | null;
  confirm: number | null;
}

/**
 * The admin's own phone: the login code goes there, so it is typed twice (a typo would lock them
 * out) and the gateway refuses a phone another admin account has.
 */
function PhoneSection() {
  const { t } = useTranslation('auth', { keyPrefix: 'profile' });
  const { me } = useAuth();
  const form = useForm<PhoneValues>({ defaultValues: { countryCode: me?.countryCode ?? 1, phone: null, confirm: null } });
  const change = useResultMutation((v: PhoneValues) => adminController.update({ updateRequestDTO: { countryCode: v.countryCode, phone: v.phone } }), {
    success: t('phoneChanged'),
    invalidate: [['auth', 'me']],
    onSuccess: () => form.reset({ countryCode: form.getValues('countryCode'), phone: null, confirm: null }),
  });
  const err = form.formState.errors;
  return (
    <Section title={t('changePhone')} description={t('changePhoneHint')}>
      <form noValidate onSubmit={form.handleSubmit((v) => change.mutate(v))} className="flex max-w-sm flex-col gap-4">
        <div className="grid grid-cols-[6rem_1fr] gap-3">
          <Field label={t('countryCode')}>
            <Controller control={form.control} name="countryCode" render={({ field }) => <NumberInput prefix="+" min={1} precision={0} value={field.value} onChange={field.onChange} />} />
          </Field>
          <Field label={t('newPhone')} error={err.phone?.message}>
            <Controller
              control={form.control}
              name="phone"
              rules={{ validate: (v) => (v != null && v > 0) || t('phoneRequired') }}
              render={({ field }) => <NumberInput precision={0} value={field.value} onChange={field.onChange} />}
            />
          </Field>
          {/* under the number it confirms, not under the country code */}
          <Field label={t('confirmPhone')} error={err.confirm?.message} className="col-start-2">
            <Controller
              control={form.control}
              name="confirm"
              rules={{ validate: (v, all) => v === all.phone || t('phonesDiffer') }}
              render={({ field }) => <NumberInput precision={0} value={field.value} onChange={field.onChange} />}
            />
          </Field>
        </div>
        <Button type="submit" loading={change.isPending} className="self-start">
          {t('submitPhone')}
        </Button>
      </form>
    </Section>
  );
}
